import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import useReferralPromptDisplay from '../hooks/useReferralPromptDisplay';
import { useReferralPromptShown } from '../api/queries';

import ReferralPromptView from './ReferralPromptView';

import useEventTracking from 'modules/common/components/AnalyticsTracking/useEventTracking';
import useReferralPromoConfig from 'modules/common/hooks/useReferralPromoConfig';
import { useMember } from 'modules/common/queries/member';
import { useImpactAdvocateEnabled } from 'modules/common/hooks/useImpactAdvocateEnabled';
import { useLayoutContext } from 'modules/common/components/Layout/LayoutContext';
import { useIMCContext } from 'modules/IMC/common/context';
import useImpactEmbedTracking from 'modules/common/hooks/useImpactEmbedTracking';
import { fetchReferralShareLink, getReferralCopyUrl } from 'services/member/fetchReferralShareLink';
import { sendDatadogRumError } from 'utils/analytics/datadogRum';
import useAuthSelectors from 'modules/Auth/AuthProvider/useAuthSelector';
import useImpactJwt from 'services/impact/queries/useImpactJwt';

const COPY_FEEDBACK_MS = 2500;

export default function ReferralPrompt() {
    const [linkCopied, setLinkCopied] = useState(false);
    const copyFeedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const { shouldShow, dismiss } = useReferralPromptDisplay();
    const { sendTrackingEvent } = useEventTracking();
    const { mutate: postShown } = useReferralPromptShown();
    const { data: member } = useMember();

    const {
        data: shareLinkResponse,
        isPending: shareUrlLoading,
        refetch: refetchShareLink,
    } = useQuery({
        queryKey: ['referralShareLink', member?.id],
        queryFn: fetchReferralShareLink,
        enabled: shouldShow && !!member?.id,
        staleTime: 60_000,
    });

    const displayShareUrl = getReferralCopyUrl(shareLinkResponse ?? { share_link: null, referral_code: null });
    const isImpactAdvocateEnabled = useImpactAdvocateEnabled();
    const { isImpersonating } = useAuthSelectors('isImpersonating');
    useImpactJwt(
        member?.email ? { email: member.email, firstName: member.first_name, lastName: member.last_name } : undefined,
        { enabled: shouldShow && isImpactAdvocateEnabled && !isImpersonating }
    );
    const { hideHelpAgentFab, showHelpAgentFab } = useIMCContext();
    const { registerBottomRightPopup, unregisterBottomRightPopup } = useLayoutContext();
    useImpactEmbedTracking('referral_prompt', shouldShow);

    const { calculateRewardBalance } = useReferralPromoConfig();
    const rewardText = calculateRewardBalance(1);

    useEffect(() => {
        if (!shouldShow) {
            return undefined;
        }
        registerBottomRightPopup('referral');
        hideHelpAgentFab();
        return () => {
            unregisterBottomRightPopup('referral');
            showHelpAgentFab();
        };
    }, [shouldShow, hideHelpAgentFab, showHelpAgentFab, registerBottomRightPopup, unregisterBottomRightPopup]);

    const hasTrackedDisplayRef = useRef(false);

    useEffect(() => {
        if (!shouldShow || hasTrackedDisplayRef.current) {
            return;
        }

        hasTrackedDisplayRef.current = true;
        sendTrackingEvent('Referral Prompt Displayed', {
            category: 'Referral Prompt',
            action: 'displayed',
            location: window.location.href,
        });
    }, [shouldShow, sendTrackingEvent]);

    useEffect(() => {
        return () => {
            if (copyFeedbackTimeoutRef.current) {
                clearTimeout(copyFeedbackTimeoutRef.current);
            }
        };
    }, []);

    const handleClose = useCallback(() => {
        postShown('dismissed');
        sendTrackingEvent('Referral Prompt Dismissed', {
            category: 'Referral Prompt',
            action: 'dismissed',
            location: window.location.href,
        });
        dismiss();
    }, [dismiss, sendTrackingEvent, postShown]);

    const handleReferNow = useCallback(async () => {
        if (copyFeedbackTimeoutRef.current) {
            clearTimeout(copyFeedbackTimeoutRef.current);
            copyFeedbackTimeoutRef.current = null;
        }

        try {
            let shareData = shareLinkResponse;
            if (!shareData) {
                const refetched = await refetchShareLink();
                shareData = refetched.data ?? undefined;
            }
            const urlToCopy = getReferralCopyUrl(shareData ?? { share_link: null, referral_code: null });
            if (urlToCopy) {
                await navigator.clipboard.writeText(urlToCopy);
                setLinkCopied(true);
                copyFeedbackTimeoutRef.current = setTimeout(() => {
                    setLinkCopied(false);
                    copyFeedbackTimeoutRef.current = null;
                }, COPY_FEEDBACK_MS);
                postShown('engaged');
                sendTrackingEvent('Referral Link Copied', {
                    category: 'Referral Prompt',
                    action: 'refer_now',
                    location: window.location.href,
                });
            }
        } catch (error) {
            sendDatadogRumError('Referral prompt copy failed', error, {
                context: 'referral_prompt_copy',
            });
        }
    }, [sendTrackingEvent, postShown, shareLinkResponse, refetchShareLink]);

    if (!shouldShow) {
        return null;
    }

    return (
        <ReferralPromptView
            open={shouldShow}
            rewardText={rewardText}
            shareUrl={displayShareUrl}
            shareUrlLoading={shareUrlLoading}
            linkCopied={linkCopied}
            onClose={handleClose}
            onReferNow={handleReferNow}
        />
    );
}
